import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: 'success' | 'error' | 'info' | 'warning';
}

interface UiState {
  theme: Theme;
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  commandPaletteOpen: boolean;
  toasts: Toast[];

  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarMobileOpen: (open: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  toast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && prefersDark);
  root.classList.toggle('dark', dark);
}

const storedTheme = (localStorage.getItem('bruhat.theme') as Theme | null) ?? 'system';
applyTheme(storedTheme);

// Follow the OS when the user has not made an explicit choice.
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if ((localStorage.getItem('bruhat.theme') as Theme | null) === 'system') applyTheme('system');
});

export const useUiStore = create<UiState>((set, get) => ({
  theme: storedTheme,
  sidebarCollapsed: localStorage.getItem('bruhat.sidebarCollapsed') === 'true',
  sidebarMobileOpen: false,
  commandPaletteOpen: false,
  toasts: [],

  setTheme(theme) {
    localStorage.setItem('bruhat.theme', theme);
    applyTheme(theme);
    set({ theme });
  },

  toggleSidebar() {
    const collapsed = !get().sidebarCollapsed;
    localStorage.setItem('bruhat.sidebarCollapsed', String(collapsed));
    set({ sidebarCollapsed: collapsed });
  },

  setSidebarMobileOpen(open) {
    set({ sidebarMobileOpen: open });
  },

  setCommandPaletteOpen(open) {
    set({ commandPaletteOpen: open });
  },

  toast(toast) {
    const id = crypto.randomUUID();
    set({ toasts: [...get().toasts, { ...toast, id }] });
    // Errors stay longer — they usually need reading.
    const timeout = toast.variant === 'error' ? 8000 : 4000;
    setTimeout(() => get().dismissToast(id), timeout);
  },

  dismissToast(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));
